/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { CreateModelDto, UpdateModelDto } from "data/dto";
import { migrateBannerimage } from "data/dto/model/transformers/migrate-banner-image";
import { removeAuditDates } from "data/dto/model/transformers/remove-audit-dates";
import { Logger, green } from "cms/utils/log/logger";
import Config from "data/config";

export const updateModel = createAsyncThunk(
    "model/update",

    /**
     * @param {object} arg
     * @param {string} arg.token
     * @param {ModelUpdates} arg.update
     * @param thunkAPI
     */
    async function (arg, thunkAPI) {
        
        const token = arg?.token;
        const update = arg?.update;
        const dispatch = thunkAPI?.dispatch;
        
        /** @type {RootState} */
        const state = thunkAPI.getState();

        if (Config.Logging.enableSavePipelineLog) {
            Logger.debug(green("[updateModel] createAsyncThunk [model/update]:", { state }));
        }
        
        // Loading state needs to wrap both the update and subsequent fetch
        // also see src/data/store/reducers/model/model.slice.js
        dispatch({ type: "model/setLoading", payload: true });

        const payload = UpdateModelDto(state.model.current, update);
        
        // fix cases where content is using old name for banner_image (banner-image)
        migrateBannerimage(payload);
        
        // remove updated_at and created_at; dates are done at API
        removeAuditDates(payload);

        if (Config.Logging.enableSavePipelineLog) {
            Logger.debug(green("[updateModel] SDK update:", { payload }));
        }
        
        const response = await SimpliCityAdminSDK.model.update(
            token,
            payload.guid,
            payload
        );
        
        await dispatch(fetchModel({ token, id: payload.guid }));
        
        // Loading state needs to wrap both the update and subsequent fetch
        // also see src/data/store/reducers/model/model.slice.js
        dispatch({ type: "model/setLoading", payload: false });

        return response;
    }
);

export const markModified = createAsyncThunk(
    "model/mark-modified",

    /**
     * @param {object} arg
     * @param {string} arg.token
     * @param {string} arg.guid
     * @param thunkAPI
     */
    async function (arg, thunkAPI) {

        const token = arg?.token;
        const guid = arg?.guid;
        const dispatch = thunkAPI?.dispatch;

        /** @type {RootState} */
        const state = thunkAPI.getState();

        if (Config.Logging.enableSavePipelineLog) {
            Logger.debug(green("[updateModel] createAsyncThunk [model/mark-modified]:", { state }));
        }

        // Loading state needs to wrap both the update and subsequent fetch
        // also see src/data/store/reducers/model/model.slice.js
        dispatch({ type: "model/setLoading", payload: true });

        if (Config.Logging.enableSavePipelineLog) {
            Logger.debug(green(`[updateModel] SDK mark-modified: ${guid}`));
        }

        const response = await SimpliCityAdminSDK.model.markModified(token, guid);

        await dispatch(fetchModel({ token, id: guid }));

        // Loading state needs to wrap both the update and subsequent fetch
        // also see src/data/store/reducers/model/model.slice.js
        dispatch({ type: "model/setLoading", payload: false });

        return response;
    }
);

export const createModel = createAsyncThunk(
    "model/create",
    
    /**
     * @param {object} arg
     * @param {string} arg.token
     * @param {string=} arg.decription
     * @param thunkAPI
     */
    async function (arg, thunkAPI) {
        const description = arg?.description;
        const token = arg?.token;
        
        /** @type {RootState} */
        const state = thunkAPI.getState();
        
        const model = state?.model?.current;
        const payload = CreateModelDto(model?.title, description);
        
        const response = await SimpliCityAdminSDK.model.create(
            token,
            payload
        );
        
        return response;
    }
);

export const fetchModel = createAsyncThunk(
    "model/fetch",
    
    /**
     * @param {object} arg
     * @param {string} arg.token
     * @param {string} arg.id
     * @param thunkAPI
     */
    async function (arg, thunkAPI) {
        const token = arg?.token;
        const id = arg?.id;
        const dispatch = thunkAPI?.dispatch;
        const model = await SimpliCityAdminSDK.model.findOne(token, id);
        // also initialize composer field state
        dispatch({ type: "composer/initFieldState", payload: model });
        return model;
    }
);


/**
 * @typedef {import("./model.state")["modelState"]} ModelState
 */

/**
 * @typedef {object} RootState
 * @property {ModelState} model
 * @property {WebpageState} webpage
 */

/**
 * @typedef {object} ModelUpdates
 * @property {object=} field
 * @property {string=} title
 * @property {string=} description
 */
